import { useCallback, useMemo } from "react"

import * as turf from "@turf/turf"

import { MapWithPhotoViewer } from "@l2r-front/l2r-common"
import { useImageCollectionDispatchContext, useImageCollectionStateContext } from "@l2r-front/l2r-images"
import { ReactMapGl, useMapContext } from "@l2r-front/l2r-map"
import { RoadSearch, useIsFetchingNetwork, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { useIsFetchingRiskZones } from "../../../modules/riskZones/hooks/queries/useIsFetchingRiskZones"
import { useIsFetchingSpeedings } from "../../../modules/speedings/hooks/queries/useIsFetchingSpeedings"

import { SafetyMapLegend } from "../SafetyMapLegend"

export function Map(props) {

    const {
        children,
        ...mapProps
    } = props

    const { selectedNetwork } = useNetworksStateContext()
    const [mapState, mapDispatch] = useMapContext()
    const { setMapBoundingBoxToFit } = mapDispatch
    const isMobile = useIsMobileDevice()
    const { imageLayerDisplayed } = useImageCollectionStateContext()
    const { onOpenImageViewer, onSwitchImageLayer, isImageCollectionAvailable } = useImageCollectionDispatchContext()
    const isFetchingNetwork = useIsFetchingNetwork()
    const isFetchingRiskZones = useIsFetchingRiskZones()
    const isFetchingSpeedings = useIsFetchingSpeedings()
    const isFetching = isFetchingRiskZones || isFetchingSpeedings || isFetchingNetwork

    const { error } = mapState

    const mapInitialViewState = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        return {
            longitude: (minLng + maxLng) / 2,
            latitude: (minLat + maxLat) / 2,
            zoom: 8,
        }
    }, [selectedNetwork])

    const onRecenterOnNetworkBoundingBox = useCallback(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        setMapBoundingBoxToFit({
            minLng,
            minLat,
            maxLng,
            maxLat,
        })
    }, [selectedNetwork, setMapBoundingBoxToFit])

    const onSwitchImageLayerCallback = isImageCollectionAvailable() ? onSwitchImageLayer : undefined

    return (
        <MapWithPhotoViewer
            dragRotate={false}
            error={error}
            imageLayerDisplayed={imageLayerDisplayed}
            initialViewState={mapInitialViewState}
            isBaseMap
            isLoading={isFetching}
            onOpenImageViewer={onOpenImageViewer}
            onSwitchImageLayer={onSwitchImageLayerCallback}
            onRecenter={onRecenterOnNetworkBoundingBox}
            topLeftElements={[<RoadSearch key="road-search" disableExpend={!isMobile} />, <SafetyMapLegend key="safety-map-legend" />]}
            {...mapProps}
        >
            {children}
        </MapWithPhotoViewer>
    )
}

Map.propTypes = {
    ...ReactMapGl.propTypes,
    className: PropTypes.string,
}